import { FC, useState } from 'react';
import { css } from '@emotion/react';
import clsx from 'clsx';
import { Col, Row } from 'reactstrap';

import commonVars from '@/common/assets/scss/abstracts/_exports.module.scss';
import ShadowBox from '@/common/components/general/ShadowBox';
import SvgRender from '@/library/components/general/SvgRender';
import arrowFilters from '@/common/assets/svg/common/arrows/arrow-filters.svg';
import arrowRight from '@/common/assets/svg/common/arrows/arrow-one-way.svg';
import showChangesIcon from '@/common/assets/svg/common/versioning.svg';
import pending from '@/common/assets/svg/common/pending.svg';
import { useCompanySettingsDraft } from './draft-context';

const UnsavedChangesBanner: FC = () => {
  const { changes, showChangesOnly, toggleShowChangesOnly } = useCompanySettingsDraft();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChanges = changes.length > 0;

  return (
    <ShadowBox
      color="lighter-blue"
      flat
      css={css`
        min-height: 50px;
      `}
      className={`cmb-12 px-2 py-1 mt-1`}
    >
      <div className="d-flex align-items-center justify-content-between">
        <div
          className={clsx(
            'd-flex align-items-center fw-bold fs-13',
            hasChanges ? 'text-warning' : 'text-primary'
          )}
        >
          <SvgRender src={pending} className="cme-6" style={{ width: 14, height: 14 }} />
          {changes.length} unsaved change{changes.length === 1 ? '' : 's'}
        </div>
        <div className="d-flex align-items-center flex-shrink-0">
          {hasChanges ? (
            <>
              <button
                type="button"
                className={clsx(
                  'd-flex align-items-center border-0 border-radius-7 cursor-pointer cpx-8 cpy-4 fs-12 fw-bold text-white me-2',
                  showChangesOnly && 'opacity-90'
                )}
                css={css`
                  background-color: ${commonVars.goldBell};
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);

                  &:hover {
                    background-color: ${commonVars.orangeWeb};
                  }
                `}
                onClick={toggleShowChangesOnly}
              >
                <SvgRender
                  src={showChangesIcon}
                  className="text-white me-1"
                  style={{ width: 12, height: 12 }}
                />
                {showChangesOnly ? 'Show All' : 'Show Changes'}
              </button>
              <button
                type="button"
                className="d-flex align-items-center border-0 bg-transparent fs-12 fw-medium text-moody-blue cursor-pointer"
                onClick={() => setIsExpanded(current => !current)}
              >
                {isExpanded ? 'Shrink' : 'Expand'}
                <SvgRender
                  src={arrowFilters}
                  className="text-moody-blue cms-6"
                  style={{
                    width: 10,
                    height: 10,
                    transform: isExpanded ? undefined : 'rotate(180deg)'
                  }}
                />
              </button>
            </>
          ) : null}
        </div>
      </div>
      {isExpanded && hasChanges ? (
        <div className="cmt-4">
          {changes.map((change, index) => {
            const showCategory =
              index === 0 || changes[index - 1]?.categoryId !== change.categoryId;

            return (
              <Row key={`${change.categoryId}-${change.key}`} className="fs-12 mb-1">
                <Col xs={3} className="text-uppercase text-violet">
                  <span className="fw-bold">{showCategory ? change.categoryTitle : ''}</span>
                </Col>
                <Col xs={3} className="text-violet fw-medium">
                  <span className="fw-medium">{change.label}</span>
                </Col>
                <Col xs={3} className="fw-medium">
                  <div className="fw-medium text-primary d-flex align-items-center">
                    {change.oldDisplay}
                    <SvgRender
                      src={arrowRight}
                      className="cms-6"
                      style={{ width: 14, height: 14 }}
                    />
                    <div className="text-turquoise cms-6">{change.newDisplay}</div>
                  </div>
                </Col>
              </Row>
            );
          })}
        </div>
      ) : null}
    </ShadowBox>
  );
};

export default UnsavedChangesBanner;
